import { useState } from 'react';
import { User, Phone, Trash2, Shield, Key } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { User as UserType } from '@/types';
import { useAuthStore } from '@/store/authStore';
import { UpdatePhoneDialog } from './UpdatePhoneDialog';
import { DeleteUserDialog } from './DeleteUserDialog';
import { PasswordDialog } from './PasswordDialog';

interface UserItemProps {
  user: UserType;
  isAdmin: boolean;
  onUpdatePhone: (userId: string, phoneNumber: string) => void;
  onUpdatePassword: (userId: string, password: string) => void;
  onDelete: (userId: string) => void;
}

export function UserItem({
  user,
  isAdmin,
  onUpdatePhone,
  onUpdatePassword,
  onDelete,
}: UserItemProps) {
  const currentUser = useAuthStore((state) => state.user);
  const [showUpdatePhone, setShowUpdatePhone] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);

  // Check if this is the current user
  const isSelf = currentUser?.id === user.id;

  return (
    <div className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div className="flex items-center">
        <span className="flex-shrink-0 h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
          {user.role === 'admin' ? (
            <Shield className="h-6 w-6 text-primary" />
          ) : (
            <User className="h-6 w-6 text-primary" />
          )}
        </span>
        <div className="mr-3">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-medium text-gray-900">
              {user.username}
            </h3>
            {user.role === 'admin' && (
              <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                مدير
              </span>
            )}
            {isSelf && (
              <span className="text-xs bg-secondary/10 text-secondary px-2 py-1 rounded-full">
                أنت
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500" dir="ltr">
            {user.phoneNumber || 'لا يوجد رقم هاتف'}
          </p>
        </div>
      </div>

      {isAdmin && (
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="hover:bg-secondary hover:text-white"
            onClick={() => setShowPasswordDialog(true)}
          >
            <Key className="h-4 w-4 ml-2" />
            تعيين كلمة المرور
          </Button>
          <Button
            variant="outline"
            className="hover:bg-secondary hover:text-white"
            onClick={() => setShowUpdatePhone(true)}
          >
            <Phone className="h-4 w-4 ml-2" />
            تحديث رقم الهاتف
          </Button>
          {!isSelf && (
            <Button
              variant="ghost"
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={() => setShowDeleteConfirm(true)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      )}

      <UpdatePhoneDialog
        open={showUpdatePhone}
        onClose={() => setShowUpdatePhone(false)}
        onSubmit={(phone) => {
          onUpdatePhone(user.id, phone);
          setShowUpdatePhone(false);
        }}
        initialPhone={user.phoneNumber}
      />

      <DeleteUserDialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => {
          onDelete(user.id);
          setShowDeleteConfirm(false);
        }}
      />

      <PasswordDialog
        open={showPasswordDialog}
        onClose={() => setShowPasswordDialog(false)}
        onSubmit={(password) => {
          onUpdatePassword(user.id, password);
          setShowPasswordDialog(false);
        }}
        username={user.username}
      />
    </div>
  );
}